import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/lib/store";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    Loader2,
    Search,
    Globe,
    Instagram,
    Facebook,
    Linkedin,
    Phone,
    MapPin,
    Building2,
    FileText,
    CheckCircle2,
    XCircle,
    Edit3,
    RefreshCw,
    ExternalLink,
    Sparkles,
    Image as ImageIcon,
    MapPinned,
    ArrowRight,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    Star,
    Circle,
    ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AnalysisProgress from "@/components/AnalysisProgress";

const FIELD_META = {
    website: { label: "Website", icon: Globe },
    google_business: { label: "Google Business Profile", icon: MapPinned },
    instagram: { label: "Instagram", icon: Instagram },
    facebook: { label: "Facebook", icon: Facebook },
    linkedin: { label: "LinkedIn", icon: Linkedin },
    phone: { label: "Phone", icon: Phone },
    address: { label: "Address", icon: MapPin },
    category: { label: "Category", icon: Building2 },
    description: { label: "Description", icon: FileText },
    logo: { label: "Logo", icon: ImageIcon },
};

const FIELD_TO_BIZ = {
    website: "website_url",
    google_business: "google_business_url",
    instagram: "instagram_url",
    facebook: "facebook_url",
    linkedin: "linkedin_url",
    phone: "phone",
    address: "address",
    category: "business_category",
    description: "business_description",
    logo: "logo_url",
};

const GROUPS = [
    { title: "Profiles & links", fields: ["website", "google_business", "instagram", "facebook", "linkedin"] },
    { title: "Contact details", fields: ["phone", "address"] },
    { title: "About the business", fields: ["category", "description", "logo"] },
];
const ORDER = GROUPS.flatMap((g) => g.fields);
const URL_FIELDS = ["website", "google_business", "instagram", "facebook", "linkedin"];
const REFINABLE = URL_FIELDS;

const SEARCH_STAGES = [
    "Looking up Google Maps listings",
    "Searching the web for the official website",
    "Reading the website for linked profiles",
    "Searching Instagram, Facebook and LinkedIn",
    "Checking public profile pages",
    "AI cross-check of uncertain matches",
];

const CONF_STYLE = {
    HIGH: "border-emerald-500/40 bg-emerald-500/10 text-emerald-100",
    MEDIUM: "border-amber-500/40 bg-amber-500/10 text-amber-100",
    LOW: "border-rose-500/40 bg-rose-500/10 text-rose-100",
};

function ConfidenceBadge({ level, testid }) {
    if (!level) return null;
    const label = { HIGH: "High match", MEDIUM: "Likely", LOW: "Unsure" }[level] || level;
    return (
        <span
            data-testid={testid}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold ${CONF_STYLE[level]}`}
        >
            {label}
        </span>
    );
}

function displayValue(fkey, f) {
    if (!f?.value) return null;
    if (fkey === "instagram" && f.meta?.handle) return `@${f.meta.handle}`;
    if (fkey === "google_business" && f.meta?.display_name) return f.meta.display_name;
    return f.value;
}

function isUrl(v) {
    return typeof v === "string" && /^https?:\/\//.test(v);
}

export default function Onboarding() {
    const { loadBusinesses, businesses } = useApp();
    const nav = useNavigate();
    const [step, setStep] = useState("input"); // input | searching | branch | review | analysing
    // The public site's "Check my business" form hands its values over here.
    const [prefill] = useState(() => {
        try {
            return JSON.parse(sessionStorage.getItem("ll_prefill") || "null") || {};
        } catch {
            return {};
        }
    });
    const [name, setName] = useState(prefill.name || "");
    const [city, setCity] = useState(prefill.city || "");
    const [description, setDescription] = useState("");
    useEffect(() => {
        sessionStorage.removeItem("ll_prefill");
    }, []);
    const [providers, setProviders] = useState(null);
    const [result, setResult] = useState(null);
    const [fields, setFields] = useState({});
    const [rejected, setRejected] = useState({});
    const [openAlts, setOpenAlts] = useState({});
    const [editField, setEditField] = useState(null);
    const [refineField, setRefineField] = useState(null);
    const [refineOptions, setRefineOptions] = useState([]);
    const [refineFeedback, setRefineFeedback] = useState("");
    const [refineBusy, setRefineBusy] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [analysis, setAnalysis] = useState(null);
    const [newBizId, setNewBizId] = useState(null);
    const pollRef = useRef(null);

    useEffect(() => {
        api.discoveryProviders().then(setProviders).catch(() => setProviders(null));
        return () => pollRef.current && clearInterval(pollRef.current);
    }, []);

    // ------------------------------------------------------------ search
    const runSearch = async () => {
        if (!name.trim() || !city.trim()) {
            toast.error("Company name and city are both needed.");
            return;
        }
        setStep("searching");
        try {
            const r = await api.discoverySearch(name.trim(), city.trim(), description.trim() || null);
            setResult(r);
            const init = {};
            ORDER.forEach((k) => {
                const v = r.fields?.[k] || {};
                init[k] = { ...v, state: v.value ? "pending" : "empty" };
            });
            setFields(init);
            setRejected({});
            setOpenAlts({});
            if ((r.candidates || []).length >= 2 && r.primary_index === null) {
                setStep("branch");
            } else {
                setStep("review");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Search failed — please try again.");
            setStep("input");
        }
    };

    const applyCandidate = (c) => {
        const ev = c.reasons || [];
        const next = { ...fields };
        const put = (k, value, meta = {}) => {
            if (!value) return;
            next[k] = {
                ...(next[k] || {}),
                value,
                confidence: "HIGH",
                evidence: ["You picked this location", ...ev],
                warnings: [],
                meta: { ...(next[k]?.meta || {}), ...meta },
                state: "approved",
            };
        };
        put("google_business", c.google_business, { place_id: c.place_id, display_name: c.headline, rating: c.rating, review_count: c.review_count });
        put("phone", c.phone);
        put("address", c.address);
        put("category", c.category);
        if (c.website) put("website", c.website.replace(/\/$/, ""));
        setFields(next);
        setStep("review");
    };

    // ------------------------------------------------------------ field actions
    const setState = (fkey, state) =>
        setFields((s) => ({ ...s, [fkey]: { ...s[fkey], state } }));

    const approveField = (fkey) => setState(fkey, fields[fkey]?.state === "approved" ? "pending" : "approved");

    const rejectField = (fkey) => {
        const cur = fields[fkey];
        if (cur?.value) setRejected((s) => ({ ...s, [cur.value]: true }));
        const alts = (cur?.alternatives || []).filter((a) => a.value !== cur?.value);
        setFields((s) => ({
            ...s,
            [fkey]: {
                value: null, confidence: null, evidence: [], warnings: [], meta: {},
                alternatives: cur?.value ? [{ value: cur.value, confidence: cur.confidence, evidence: cur.evidence || [], meta: cur.meta, rejected: true }, ...alts] : alts,
                state: "rejected",
            },
        }));
        if (alts.length) setOpenAlts((s) => ({ ...s, [fkey]: true }));
    };

    const pickAlternative = (fkey, alt) => {
        setFields((s) => {
            const cur = s[fkey] || {};
            const others = (cur.alternatives || []).filter((a) => a.value !== alt.value);
            if (cur.value) others.unshift({ value: cur.value, confidence: cur.confidence, evidence: cur.evidence, warnings: cur.warnings, meta: cur.meta });
            return {
                ...s,
                [fkey]: { ...cur, value: alt.value, confidence: alt.confidence, evidence: alt.evidence || [], warnings: alt.warnings || [], meta: alt.meta || {}, alternatives: others, state: "approved" },
            };
        });
        setRejected((s) => {
            const n = { ...s };
            delete n[alt.value];
            return n;
        });
    };

    const approveAllHigh = () => {
        let n = 0;
        setFields((s) => {
            const next = { ...s };
            ORDER.forEach((k) => {
                if (next[k]?.value && next[k].confidence === "HIGH" && next[k].state !== "approved") {
                    next[k] = { ...next[k], state: "approved" };
                    n += 1;
                }
            });
            return next;
        });
        setTimeout(() => toast.success(n ? `Approved ${n} high-match item${n > 1 ? "s" : ""}.` : "Nothing new to approve."), 0);
    };

    const saveEdit = async () => {
        if (!editField) return;
        const val = editField.value?.trim() || null;
        const key = editField.key;
        if (val && URL_FIELDS.includes(key)) {
            try {
                const v = await api.discoveryVerifyUrl(val, key, name);
                if (v.host_matches === false) {
                    toast.error(`That isn't a valid ${FIELD_META[key].label} link.`);
                    return;
                }
                if (!v.ok) {
                    toast.error(`URL not reachable (HTTP ${v.status}). Please check it.`);
                    return;
                }
                setFields((s) => ({
                    ...s,
                    [key]: {
                        ...s[key],
                        value: v.final_url || val,
                        confidence: "HIGH",
                        evidence: [v.unverified ? "Entered by you (platform blocks automatic checks)" : "Entered by you · link verified"],
                        warnings: [],
                        meta: { ...(v.place_id ? { place_id: v.place_id } : {}), ...(v.handle ? { handle: v.handle } : {}) },
                        state: "approved",
                    },
                }));
            } catch {
                toast.error("Couldn't verify — try again.");
                return;
            }
        } else {
            setFields((s) => ({
                ...s,
                [key]: { ...s[key], value: val, confidence: val ? "HIGH" : null, evidence: val ? ["Entered by you"] : [], warnings: [], meta: {}, state: val ? "approved" : "empty" },
            }));
        }
        setEditField(null);
    };

    const fetchRefine = async (fkey, feedback) => {
        setRefineBusy(true);
        try {
            const excludes = Object.keys(rejected);
            const cur = fields[fkey]?.value;
            if (cur) excludes.push(cur);
            const r = await api.discoveryRefine({
                name, city: city || null, description: description || null,
                field: fkey, exclude_urls: excludes, feedback: feedback || null,
            });
            setRefineOptions(r.options || []);
        } catch {
            toast.error("Search failed.");
        } finally {
            setRefineBusy(false);
        }
    };

    const openRefine = (fkey) => {
        setRefineField(fkey);
        setRefineOptions([]);
        setRefineFeedback("");
        fetchRefine(fkey, null);
    };

    const pickRefinedOption = (opt) => {
        setFields((s) => ({
            ...s,
            [refineField]: {
                ...s[refineField],
                value: opt.url,
                confidence: opt.name_match >= 0.8 ? "HIGH" : "MEDIUM",
                evidence: ["Chosen by you from search results", opt.title].filter(Boolean),
                warnings: [],
                meta: { ...(opt.place_id ? { place_id: opt.place_id, display_name: opt.title } : {}), ...(opt.handle ? { handle: opt.handle } : {}) },
                state: "approved",
            },
        }));
        setRefineField(null);
    };

    // ------------------------------------------------------------ confirm + analyse
    const counts = useMemo(() => {
        const vals = ORDER.filter((k) => fields[k]?.value);
        return {
            found: vals.length,
            approved: vals.filter((k) => fields[k].state === "approved").length,
            pending: vals.filter((k) => fields[k].state !== "approved"),
            high: vals.filter((k) => fields[k].confidence === "HIGH" && fields[k].state !== "approved").length,
        };
    }, [fields]);

    const submitConfirm = async () => {
        setSubmitting(true);
        const payload = { name: name.trim(), city: city.trim() || null };
        const confidence = {};
        const reasons = {};
        const confirmed = [];
        for (const k of ORDER) {
            const f = fields[k];
            if (f?.value && f.state === "approved") {
                payload[FIELD_TO_BIZ[k]] = f.value;
                confidence[k] = f.confidence || "HIGH";
                reasons[k] = (f.evidence || []).slice(0, 3).join(" · ");
                confirmed.push(k);
            }
        }
        if (fields.google_business?.state === "approved" && fields.google_business?.meta?.place_id) {
            payload.google_place_id = fields.google_business.meta.place_id;
        }
        if (!payload.business_description && description.trim()) {
            payload.business_description = description.trim();
        }
        payload.discovery_confidence = confidence;
        payload.discovery_reasons = reasons;
        payload.confirmed_fields = confirmed;
        payload.rejected_urls = Object.keys(rejected);
        try {
            const r = await api.discoveryConfirm(payload);
            const bizId = r.business?.id;
            setNewBizId(bizId);
            if (bizId) localStorage.setItem("ll_biz_id", bizId);
            await loadBusinesses();
            if (r.analysis?.id) {
                setAnalysis({ id: r.analysis.id, status: "running", steps: [] });
                setStep("analysing");
                startPolling(bizId, r.analysis.id);
            } else {
                nav("/presence");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally {
            setSubmitting(false);
        }
    };

    const startPolling = (bizId, aid) => {
        if (pollRef.current) clearInterval(pollRef.current);
        const tick = async () => {
            try {
                const r = await api.presenceGet(bizId, aid);
                const a = r.analysis;
                setAnalysis(a);
                const stepsDone = (a.steps || []).every((s) => ["done", "failed"].includes(s.status));
                if (a.status === "failed" || (a.status === "done" && stepsDone)) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    if (a.status === "done") {
                        toast.success("Analysis ready.");
                        setTimeout(() => nav("/presence"), 600);
                    }
                }
            } catch {
                /* keep polling */
            }
        };
        tick();
        pollRef.current = setInterval(tick, 2500);
    };

    // ------------------------------------------------------------ render
    const heading = {
        input: ["Step 1 · Tell us about the business", "Which business are you setting up?"],
        searching: ["Step 2 · Detecting online presence", `Finding ${name} online…`],
        branch: ["Step 2 · Pick the right location", "We found more than one matching location"],
        review: ["Step 3 · Approve what we found", "Is this your business online?"],
        analysing: ["Step 4 · Analysing", "Analysing each profile"],
    }[step];

    return (
        <div className="min-h-screen bg-[#090d16] grain text-slate-100">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 pb-32">
                <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="label-eyebrow text-indigo-300">{heading[0]}</div>
                        <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold">{heading[1]}</h1>
                        {step === "review" && (
                            <p className="mt-2 text-sm text-slate-400">
                                Only items you approve are saved and analysed. Every link was found in a real source — the evidence under each one shows where.
                            </p>
                        )}
                    </div>
                    {businesses?.length > 0 && step === "input" && (
                        <Button variant="outline" onClick={() => nav("/dashboard")} className="border-white/10 bg-white/[0.03] text-slate-200">
                            Back to dashboard
                        </Button>
                    )}
                </div>

                {(providers?.warnings || []).length > 0 && ["input", "review"].includes(step) && (
                    <div data-testid="provider-warning" className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-100 space-y-1">
                        <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Detection accuracy is limited on this server</div>
                        {providers.warnings.map((w, i) => <div key={i} className="text-amber-100/80">{w}</div>)}
                    </div>
                )}

                {step === "input" && (
                    <div data-testid="onboarding-name-step" className="surface rounded-2xl p-6 space-y-5">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs mono uppercase tracking-widest text-slate-500">Company name *</label>
                                <Input
                                    data-testid="onboarding-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Parekh Eye Clinic"
                                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                                    className="mt-1 bg-white/[0.03] border-white/10 text-slate-100 h-11"
                                />
                            </div>
                            <div>
                                <label className="text-xs mono uppercase tracking-widest text-slate-500">City *</label>
                                <Input
                                    data-testid="onboarding-city-input"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="e.g. Ahmedabad"
                                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                                    className="mt-1 bg-white/[0.03] border-white/10 text-slate-100 h-11"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs mono uppercase tracking-widest text-slate-500">Short description (optional)</label>
                            <Textarea
                                data-testid="onboarding-description-input"
                                rows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="e.g. Eye hospital in Navrangpura — cataract, LASIK and retina care"
                                className="mt-1 bg-white/[0.03] border-white/10 text-slate-100"
                            />
                            <div className="mt-1 text-[11px] text-slate-500">
                                Helps tell your business apart from others with a similar name.
                            </div>
                        </div>
                        <Button
                            data-testid="onboarding-search-btn"
                            disabled={!name.trim() || !city.trim()}
                            onClick={runSearch}
                            className="bg-indigo-500 hover:bg-indigo-400 text-white gap-2 h-11"
                        >
                            <Search className="h-4 w-4" />
                            Find online presence
                        </Button>
                    </div>
                )}

                {step === "searching" && (
                    <div data-testid="onboarding-searching" className="surface rounded-2xl p-6">
                        <div className="flex items-center gap-3 text-sm text-slate-200">
                            <Loader2 className="h-4 w-4 animate-spin text-indigo-300" />
                            This usually takes 15–40 seconds.
                        </div>
                        <ul className="mt-5 space-y-2.5">
                            {SEARCH_STAGES.map((s) => (
                                <li key={s} className="flex items-center gap-3 text-sm text-slate-400">
                                    <Circle className="h-3 w-3 text-slate-600" /> {s}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {step === "branch" && (
                    <div data-testid="onboarding-candidates-step" className="space-y-3">
                        <p className="text-sm text-slate-400">Pick the listing that is yours. Your website and social profiles are detected separately on the next screen.</p>
                        {(result?.candidates || []).map((c, i) => (
                            <button
                                key={i}
                                data-testid={`candidate-${i}`}
                                onClick={() => applyCandidate(c)}
                                className="w-full text-left surface rounded-2xl p-5 hover:border-indigo-500/40 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-100">{c.headline}</span>
                                            {c.rating && (
                                                <span className="inline-flex items-center gap-1 text-xs text-amber-200">
                                                    <Star className="h-3 w-3 fill-amber-300 text-amber-300" /> {c.rating} ({c.review_count || 0})
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 text-xs text-slate-400">{c.address}</div>
                                        <div className="mt-1 text-xs text-slate-500 flex gap-3 flex-wrap">
                                            {c.phone && <span>{c.phone}</span>}
                                            {c.category && <span>{c.category}</span>}
                                            {c.website && <span className="text-cyan-300">{c.website}</span>}
                                        </div>
                                    </div>
                                    <ArrowRight className="h-4 w-4 text-indigo-300 shrink-0 mt-1" />
                                </div>
                            </button>
                        ))}
                        <Button
                            data-testid="candidates-skip-btn"
                            variant="outline"
                            onClick={() => setStep("review")}
                            className="border-white/10 bg-white/[0.03] text-slate-200"
                        >
                            None of these — continue
                        </Button>
                    </div>
                )}

                {step === "review" && (
                    <div data-testid="onboarding-confirm-step" className="space-y-6">
                        {result?.status === "not_found" && (
                            <div className="surface rounded-2xl p-5 flex items-start gap-3 border-amber-500/30">
                                <AlertTriangle className="h-4 w-4 text-amber-300 mt-1 shrink-0" />
                                <div className="text-sm text-amber-100">
                                    We couldn't find “{name}” in {city} in any source. Check the spelling, add a description, or paste your links with Edit.
                                </div>
                            </div>
                        )}
                        <div className="surface rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-sm text-slate-300">
                                Found <b className="text-slate-50">{counts.found}</b> of {ORDER.length} details ·{" "}
                                <b className="text-emerald-300">{counts.approved}</b> approved
                                {result?.ai_check?.used && <span className="ml-2 text-xs text-indigo-300">· AI cross-checked</span>}
                            </div>
                            <Button
                                data-testid="approve-all-high-btn"
                                size="sm"
                                disabled={!counts.high}
                                onClick={approveAllHigh}
                                className="bg-emerald-500/15 border border-emerald-500/40 hover:bg-emerald-500/25 text-emerald-100 gap-1.5"
                            >
                                <ShieldCheck className="h-3.5 w-3.5" /> Approve all high matches ({counts.high})
                            </Button>
                        </div>

                        {GROUPS.map((g) => (
                            <section key={g.title} className="space-y-2.5">
                                <div className="label-eyebrow">{g.title}</div>
                                {g.fields.map((fkey) => (
                                    <FieldRow
                                        key={fkey}
                                        fkey={fkey}
                                        f={fields[fkey] || {}}
                                        altsOpen={!!openAlts[fkey]}
                                        toggleAlts={() => setOpenAlts((s) => ({ ...s, [fkey]: !s[fkey] }))}
                                        onApprove={() => approveField(fkey)}
                                        onReject={() => rejectField(fkey)}
                                        onEdit={() => setEditField({ key: fkey, value: fields[fkey]?.value || "" })}
                                        onRefine={() => openRefine(fkey)}
                                        onUseAlt={(a) => pickAlternative(fkey, a)}
                                    />
                                ))}
                            </section>
                        ))}
                    </div>
                )}

                {step === "analysing" && (
                    <AnalysisProgress analysis={analysis} onOpen={() => nav("/presence")} onHome={() => nav("/dashboard")} bizId={newBizId} />
                )}
            </div>

            {step === "review" && (
                <div className="fixed bottom-0 inset-x-0 border-t border-white/10 bg-[#090d16]/95 backdrop-blur">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs text-slate-400">
                            {counts.pending.length > 0 ? (
                                <span className="text-amber-200">
                                    {counts.pending.length} found item{counts.pending.length > 1 ? "s" : ""} not approved yet — {counts.pending.length > 1 ? "they" : "it"} won't be saved.
                                </span>
                            ) : (
                                <span>{counts.approved} item{counts.approved === 1 ? "" : "s"} approved.</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                data-testid="confirm-back-btn"
                                onClick={() => setStep("input")}
                                className="border-white/10 bg-white/[0.03] text-slate-200"
                            >
                                Start over
                            </Button>
                            <Button
                                data-testid="confirm-submit-btn"
                                disabled={submitting || counts.approved === 0}
                                onClick={submitConfirm}
                                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                Approve & analyse
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit dialog */}
            <Dialog open={!!editField} onOpenChange={(o) => !o && setEditField(null)}>
                <DialogContent data-testid="edit-dialog" className="bg-[#0B0F19] border-white/10 text-slate-100">
                    <DialogHeader>
                        <DialogTitle>Edit {editField?.key && FIELD_META[editField.key]?.label}</DialogTitle>
                    </DialogHeader>
                    {editField?.key === "description" ? (
                        <Textarea
                            data-testid="edit-dialog-input"
                            rows={4}
                            value={editField?.value || ""}
                            onChange={(e) => setEditField({ ...editField, value: e.target.value })}
                            className="bg-white/[0.03] border-white/10 text-slate-100"
                        />
                    ) : (
                        <Input
                            data-testid="edit-dialog-input"
                            value={editField?.value || ""}
                            onChange={(e) => setEditField({ ...editField, value: e.target.value })}
                            className="bg-white/[0.03] border-white/10 text-slate-100"
                            placeholder={URL_FIELDS.includes(editField?.key) ? "Paste the full link" : ""}
                        />
                    )}
                    {URL_FIELDS.includes(editField?.key) && (
                        <div className="text-xs text-slate-500">We check the link belongs to {FIELD_META[editField.key]?.label} before saving.</div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditField(null)} className="border-white/10 bg-white/[0.03] text-slate-200">Cancel</Button>
                        <Button data-testid="edit-dialog-save" onClick={saveEdit} className="bg-indigo-500 hover:bg-indigo-400 text-white">Save & approve</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Refine dialog */}
            <Dialog open={!!refineField} onOpenChange={(o) => !o && setRefineField(null)}>
                <DialogContent data-testid="refine-dialog" className="bg-[#0B0F19] border-white/10 text-slate-100 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Search again for {refineField && FIELD_META[refineField]?.label}</DialogTitle>
                    </DialogHeader>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Optional hint — e.g. 'Bodakdev branch' or 'old name Parekh Eye Care'"
                            value={refineFeedback}
                            onChange={(e) => setRefineFeedback(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && fetchRefine(refineField, refineFeedback)}
                            className="bg-white/[0.03] border-white/10 text-slate-100"
                        />
                        <Button
                            data-testid="refine-run-btn"
                            onClick={() => fetchRefine(refineField, refineFeedback)}
                            disabled={refineBusy}
                            className="bg-cyan-500/15 border border-cyan-500/40 hover:bg-cyan-500/25 text-cyan-100 gap-2 shrink-0"
                        >
                            {refineBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            Search
                        </Button>
                    </div>
                    <div className="max-h-80 overflow-y-auto space-y-2 mt-2">
                        {refineBusy ? (
                            <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
                        ) : refineOptions.length === 0 ? (
                            <div className="text-center py-6 text-sm text-slate-500">No results — use Edit to paste the link yourself.</div>
                        ) : refineOptions.map((o, i) => (
                            <button
                                key={i}
                                data-testid={`refine-option-${i}`}
                                onClick={() => pickRefinedOption(o)}
                                className="w-full text-left rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] p-3"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm text-slate-100 truncate">{o.title || o.url}</div>
                                    {typeof o.name_match === "number" && (
                                        <span className="text-[10px] mono text-slate-400 shrink-0">{Math.round(o.name_match * 100)}% name match</span>
                                    )}
                                </div>
                                <div className="text-xs text-cyan-300 break-all">{o.url}</div>
                                {o.snippet && <div className="text-xs text-slate-500 mt-1">{o.snippet.slice(0, 160)}</div>}
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function FieldRow({ fkey, f, altsOpen, toggleAlts, onApprove, onReject, onEdit, onRefine, onUseAlt }) {
    const cfg = FIELD_META[fkey];
    const Icon = cfg.icon;
    const approved = f.state === "approved";
    const shown = displayValue(fkey, f);
    const alts = (f.alternatives || []).filter((a) => a.value && a.value !== f.value);
    const metaBits = [];
    if (fkey === "google_business" && f.meta?.rating) metaBits.push(`★ ${f.meta.rating} · ${f.meta.review_count || 0} reviews`);
    if (f.meta?.followers != null) metaBits.push(`${Number(f.meta.followers).toLocaleString()} followers`);
    if (fkey === "linkedin" && f.meta?.kind === "person") metaBits.push("Personal profile");

    return (
        <div
            data-testid={`confirm-row-${fkey}`}
            className={`surface rounded-2xl p-4 transition-colors ${approved ? "border-emerald-500/30 bg-emerald-500/[0.03]" : ""}`}
        >
            <div className="flex items-start gap-x-3 gap-y-3 flex-wrap sm:flex-nowrap">
                <div className={`h-9 w-9 rounded-lg border flex items-center justify-center shrink-0 ${approved ? "bg-emerald-500/10 border-emerald-500/30" : "bg-white/[0.03] border-white/5"}`}>
                    <Icon className={`h-4 w-4 ${approved ? "text-emerald-300" : "text-slate-300"}`} />
                </div>
                <div className="flex-1 min-w-[calc(100%-3rem)] sm:min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-100">{cfg.label}</span>
                        {f.value && <ConfidenceBadge level={f.confidence} testid={`confidence-${fkey}`} />}
                        {approved && (
                            <span data-testid={`confirmed-${fkey}`} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-200">
                                <CheckCircle2 className="h-3 w-3" /> Approved
                            </span>
                        )}
                        {f.state === "rejected" && (
                            <span className="text-[10px] font-bold text-rose-300">Marked “not ours”</span>
                        )}
                    </div>
                    <div className="mt-1 text-sm break-all" data-testid={`value-${fkey}`}>
                        {shown ? (
                            isUrl(f.value) ? (
                                <a href={f.value} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline inline-flex items-center gap-1">
                                    {shown} <ExternalLink className="h-3 w-3 shrink-0" />
                                </a>
                            ) : fkey === "logo" ? (
                                <span className="text-slate-300">{shown}</span>
                            ) : (
                                <span className="text-slate-200">{shown}</span>
                            )
                        ) : (
                            <span className="italic text-slate-500">
                                Not found{REFINABLE.includes(fkey) ? " — search again or paste the link" : " — add it with Edit"}.
                            </span>
                        )}
                    </div>
                    {metaBits.length > 0 && (
                        <div className="mt-1 text-[11px] text-slate-400">{metaBits.join(" · ")}</div>
                    )}
                    {(f.evidence?.length > 0 || f.warnings?.length > 0) && f.value && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {(f.evidence || []).slice(0, 4).map((e, i) => (
                                <span key={`e${i}`} className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] ${e.startsWith("AI check") ? "bg-indigo-500/10 text-indigo-200 border border-indigo-500/20" : "bg-white/[0.04] text-slate-300 border border-white/5"}`}>
                                    {e.startsWith("AI check") ? <Sparkles className="h-2.5 w-2.5" /> : <CheckCircle2 className="h-2.5 w-2.5 text-emerald-400" />}
                                    {e}
                                </span>
                            ))}
                            {(f.warnings || []).slice(0, 2).map((w, i) => (
                                <span key={`w${i}`} className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] bg-amber-500/10 text-amber-200 border border-amber-500/20">
                                    <AlertTriangle className="h-2.5 w-2.5" /> {w}
                                </span>
                            ))}
                        </div>
                    )}
                    {alts.length > 0 && (
                        <button onClick={toggleAlts} className="mt-2 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200" data-testid={`alts-toggle-${fkey}`}>
                            {altsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {altsOpen ? "Hide" : "Show"} {alts.length} other possible match{alts.length > 1 ? "es" : ""}
                        </button>
                    )}
                    {altsOpen && alts.length > 0 && (
                        <div className="mt-2 space-y-1.5">
                            {alts.map((a, i) => (
                                <div key={i} className="flex items-start justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.02] p-2.5" data-testid={`alt-${fkey}-${i}`}>
                                    <div className="min-w-0">
                                        <div className="text-xs break-all">
                                            {isUrl(a.value) ? (
                                                <a href={a.value} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">{a.meta?.handle ? `@${a.meta.handle}` : a.value}</a>
                                            ) : <span className="text-slate-200">{a.value}</span>}
                                            {a.rejected && <span className="ml-2 text-[10px] text-rose-300">(you rejected this)</span>}
                                        </div>
                                        <div className="mt-0.5 text-[10.5px] text-slate-500">
                                            {a.confidence && <span className="mr-2">{{ HIGH: "High match", MEDIUM: "Likely", LOW: "Unsure" }[a.confidence]}</span>}
                                            {(a.evidence || []).slice(0, 2).join(" · ")}
                                            {(a.warnings || []).length > 0 && <span className="text-amber-300"> · {a.warnings[0]}</span>}
                                        </div>
                                    </div>
                                    <Button size="sm" variant="outline" onClick={() => onUseAlt(a)} className="h-7 px-2 text-[11px] border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200 shrink-0">
                                        Use this
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1 shrink-0 pl-12 sm:pl-0">
                    <Button
                        size="sm"
                        data-testid={`confirm-btn-${fkey}`}
                        disabled={!f.value}
                        onClick={onApprove}
                        title={approved ? "Undo approval" : "Approve"}
                        className={`h-8 px-2.5 gap-1 text-xs ${approved ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950" : "bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 text-emerald-100"}`}
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" /> {approved ? "Approved" : "Approve"}
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`edit-btn-${fkey}`} onClick={onEdit} title="Edit" className="h-8 px-2 border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-200">
                        <Edit3 className="h-3.5 w-3.5" />
                    </Button>
                    {REFINABLE.includes(fkey) && (
                        <Button size="sm" variant="outline" data-testid={`refine-btn-${fkey}`} onClick={onRefine} title="Search again" className="h-8 px-2 border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-100">
                            <RefreshCw className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`reject-btn-${fkey}`} disabled={!f.value} onClick={onReject} title="Not ours" className="h-8 px-2 border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/20 text-rose-100">
                        <XCircle className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
